import React, { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Header } from '../components/layout/Header';
import { Footer } from '../components/layout/Footer';
import { Button } from '../components/ui/Button';
import { CheckIcon } from 'lucide-react';
import { trackPageView, trackClick } from '../utils/analytics';
type ResultType = 'early' | 'mid' | 'late';
interface ResultContent {
  badge: string;
  summary: string;
  protocolTitle: string;
  protocolPoints: string[];
  crownControlIntro: string;
}
const resultContent: Record<ResultType, ResultContent> = {
  early: {
    badge: 'Early-Stage Saver',
    summary: 'Good news: based on your answers, you still have significant "follicle capital" left. You\'re the early stages of thinning, which means more of your follicles are likely dormant, not dead, and that gives you far more leverage if you act now, instead of waiting Here\'s what we recommend for your situation:',
    protocolPoints: ['Focus on stabilizing and energizing follicles before they cross the point of no return.', 'Use a non-hormonal photobiomodulation device daily to support follicle energy and keep more hairs in the growth phase.', 'Commit to a 4–6 month horizon. Hair biology moves slowly but consistently when you hit it with the right signal.'],
    crownControlIntro: "CrownControl™ is our at-home red-light protocol designed specifically for men in your situation – early to moderate thinning who don't want to gamble with their hormones, delivering targeted light at the validated wavelength range used in clinical studies, a simple 10-minute - a - day routine, you can do at home"
  },
  mid: {
    badge: 'Mid-Stage Fighter',
    summary: 'Based on your answers, you\'re at the moderate stage: clearly, visible thinning, but still, enough "follicle capital": left, that the right protocol can make a meaningful difference. The window is narrower, but you still have leverage if you use it In your situation, the realistic goal is:',
    protocolPoints: ['Stabilize ongoing loss as much as possible.', 'Thicken and strengthen miniaturized hairs that are still alive but underpowered.', 'Be honest: aim for visible improvement, not "teenage hair."'],
    crownControlIntro: 'This is where a non-hormonal red-light protocol like CrownControl™ makes sense as a foundation. It targets follicle energy at the root – without touching your hormones – and fits into a daily 10-minute routine. Many men at your stage also choose to discuss additional options like minoxidil with their doctor; CrownControl™ can operate alongside that as a separate pathway.'
  },
  late: {
    badge: 'Late-Stage Stabilizer',
    summary: "Your answers suggest you're at the later stages of loss, at least some areas, likely means some follicles are past the point of full regrowth, but you can still protect, and improve what you have left, especially borderline areas The focus for you should be:",
    protocolPoints: ['Protecting remaining follicles from further energy decline.', 'Improving scalp environment and reducing inflammation.', 'Thinking of any regrowth as upside, with "stabilize and protect" as the primary win.'],
    crownControlIntro: "CrownControl™ gives you a non-hormonal way to send a consistent, pro-growth energy signal to the follicles you still have. It's also a logical prep, and maintenance, tool if you ever decide to pursue a transplant, later: healthier follicles respond better, and are worth protecting"
  }
};
export function ResultsPage() {
  const [searchParams] = useSearchParams();
  const type = (searchParams.get('type') || 'mid') as ResultType;
  const content = resultContent[type] || resultContent.mid;
  useEffect(() => {
    trackPageView('results');
  }, []);
  const handleCTAClick = () => {
    trackClick('viewProtocol');
    window.open('https://offer.crowncontrol.shop/', '_blank');
  };
  return <div className="min-h-screen w-full bg-crown-bg flex flex-col" data-id="element-213">
      <Header data-id="element-214" />

      <main className="flex-1 py-12 sm:py-16" data-id="element-215">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-216">
          {/* Hero Section */}
          <div className="text-center mb-12 sm:mb-16" data-id="element-217">
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-crown-text mb-6" data-id="element-218">
              Your Hair Retention Score
            </h1>

            <div className="inline-block px-6 py-3 bg-crown-red rounded-full mb-8" data-id="element-219">
              <span className="text-lg sm:text-xl font-bold text-white" data-id="element-220">
                {content.badge}
              </span>
            </div>

            <p className="text-lg sm:text-xl text-crown-body leading-relaxed max-w-3xl mx-auto" data-id="element-221">
              {content.summary}
            </p>
          </div>

          {/* Protocol Section */}
          <div className="bg-crown-surface border border-crown-border rounded-2xl p-6 sm:p-8 lg:p-10 mb-8" data-id="element-222">
            <h2 className="text-2xl sm:text-3xl font-bold text-crown-text mb-6" data-id="element-223">
              Your 10-Minute Non-Hormonal Protocol
            </h2>

            <p className="text-lg text-crown-body mb-6" data-id="element-224">
              {content.protocolTitle}
            </p>

            <ul className="space-y-4 mb-8" data-id="element-225">
              {content.protocolPoints.map((point, index) => <li key={index} className="flex items-start gap-3" data-id="element-226">
                  <div className="flex-shrink-0 w-6 h-6 rounded-full bg-crown-red bg-opacity-20 flex items-center justify-center mt-0.5" data-id="element-227">
                    <CheckIcon className="w-4 h-4 text-crown-red" data-id="element-228" />
                  </div>
                  <span className="text-base text-crown-body leading-relaxed" data-id="element-229">
                    {point}
                  </span>
                </li>)}
            </ul>

            <div className="border-t border-crown-border pt-8" data-id="element-230">
              <h3 className="text-xl font-bold text-crown-text mb-4" data-id="element-231">
                Introducing CrownControl™
              </h3>
              <p className="text-base text-crown-body leading-relaxed" data-id="element-232">
                {content.crownControlIntro}
              </p>
            </div>
          </div>

          {/* CTA Block */}
          <div className="bg-crown-surface border border-crown-border rounded-2xl p-6 sm:p-8 text-center" data-id="element-233">
            <Button size="lg" className="w-full sm:w-auto mb-4" onClick={handleCTAClick} data-id="element-234">
              Start Your 10-Minute Protocol With CrownControl – $497
            </Button>
            <p className="text-sm text-crown-muted" data-id="element-235">
              FDA Cleared • Non-Hormonal • 180-Day Results Guarantee
            </p>
          </div>
        </div>
      </main>

      <Footer data-id="element-236" />
    </div>;
}